import { useRef } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';

const pullUp = {
  hidden: { y: 20, opacity: 0 },
  visible: (i) => ({ y: 0, opacity: 1, transition: { delay: i * 0.08, duration: 0.6, ease: [0.16, 1, 0.3, 1] } }),
};

// Cada palavra sobe e aparece, uma depois da outra.
export function WordsPullUp({ text, className }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  return (
    <span ref={ref} className={`words-pull ${className || ''}`}>
      {text.split(' ').map((word, i) => (
        <span className="words-pull__mask" key={`${word}-${i}`}>
          <motion.span className="words-pull__word" custom={i} variants={pullUp} initial="hidden" animate={inView ? 'visible' : 'hidden'}>
            {word}
          </motion.span>
        </span>
      ))}
    </span>
  );
}

// Igual ao anterior, mas cada trecho pode ter seu próprio estilo.
export function WordsPullUpMultiStyle({ segments, className }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const words = segments.flatMap((seg) => seg.text.split(' ').filter(Boolean).map((word) => ({ word, className: seg.className })));
  return (
    <span ref={ref} className={`words-pull words-pull--center ${className || ''}`}>
      {words.map((w, i) => (
        <span className="words-pull__mask" key={`${w.word}-${i}`}>
          <motion.span className={`words-pull__word ${w.className || ''}`} custom={i} variants={pullUp} initial="hidden" animate={inView ? 'visible' : 'hidden'}>
            {w.word}
          </motion.span>
        </span>
      ))}
    </span>
  );
}

function AnimatedLetter({ char, progress, range }) {
  const opacity = useTransform(progress, range, [0.2, 1]);
  return <motion.span style={{ opacity }}>{char}</motion.span>;
}

// Texto que vai acendendo letra por letra conforme a rolagem.
export function ScrollRevealText({ text, className }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start 0.8', 'end 0.2'] });
  const chars = Array.from(text);
  return (
    <p ref={ref} className={className} style={{ whiteSpace: 'pre-line' }}>
      {chars.map((char, i) => {
        const p = i / chars.length;
        return <AnimatedLetter key={i} char={char} progress={scrollYProgress} range={[Math.max(0, p - 0.1), Math.min(1, p + 0.05)]} />;
      })}
    </p>
  );
}

// Entrada com escala + fade, em sequência (index * 0.15s).
export function Reveal({ children, index = 0, className, as = 'div' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });
  const Tag = motion[as];
  return (
    <Tag ref={ref} className={className} initial={{ opacity: 0, scale: 0.95, y: 20 }}
      animate={inView ? { opacity: 1, scale: 1, y: 0 } : {}}
      transition={{ delay: index * 0.15, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
      {children}
    </Tag>
  );
}

export function FadeUp({ children, delay = 0, className }) {
  return (
    <motion.div className={className} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}>
      {children}
    </motion.div>
  );
}
